import {
  Avatar,
  CircularProgress,
  colors,
  IconButton,
  ListItem,
  ListItemAvatar,
  ListItemText,
  TextField
} from '@material-ui/core';
import AssignmentIcon from '@material-ui/icons/Assignment';
import BuildIcon from '@material-ui/icons/Build';
import { makeStyles } from '@material-ui/styles';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import React, { useContext, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import { useAssignJob } from 'src/firebase';
import { RegionContext } from 'src/providers/RegionProvider';
import OrderErrorBoundary, {
  ADDRESS_NEEDS_FIXING,
  compareAssignMeters,
  shopBlocksAssign,
  shopMapCenter
} from 'src/components/OrderErrorBoundary';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column'
  },
  content: {
    flexGrow: 1,
    padding: 0
  },
  avatar: {
    fontSize: 10,
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.common.white
  },
  actions: {
    justifyContent: 'flex-end'
  },
  arrowForwardIcon: {
    marginLeft: theme.spacing(1)
  },
  wrapper: {
    margin: theme.spacing(1),
    position: 'relative'
  },
  selectProgress: {
    position: 'absolute',
    top: '50%',
    right: 24,
    marginLeft: -6
  }
}));

function OrderAssignListItem({ className, job, ...rest }) {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [assign] = useAssignJob();
  const { enqueueSnackbar } = useSnackbar();

  const techs = useSelector(state => state.firestore.ordered.techs) || [];
  const shopMap = useSelector(state => state.firestore.data.shops) || {};

  const availableTechs = techs.filter(tech => tech.status === 'available');

  const { region } = useContext(RegionContext);
  const assignBlocked = shopBlocksAssign(shopMap[job.shop]);

  const getSortedTechs = currentJob => {
    return (availableTechs || [])
      .map(tech => {
        const result = assignOptionDistance(tech, currentJob, shopMap);
        return {
          ...tech,
          distance: result.meters,
          distanceBroken: result.broken
        };
      })
      .sort((a, b) => compareAssignMeters(a.distance, b.distance));
  };

  const handleChange = (evt, currentJob) => {
    if (shopBlocksAssign(shopMap[currentJob.shop])) {
      return;
    }
    setLoading(true);
    const id = evt.target.value;
    assign(region, currentJob.id, currentJob.assignee, id)
      .then(() => {
        enqueueSnackbar(
          `Job ${id === 'Unassigned' ? 'unassigned' : 'assigned'}`,
          { variant: 'success' }
        );
      })
      .catch(err => {
        enqueueSnackbar('Error assigning job: ' + err.message, {
          variant: 'error'
        });
      })
      .finally(() => setLoading(false));
  };

  return (
    <ListItem divider={true} key={job.id}>
      <ListItemAvatar>
        <IconButton
          size="small"
          component={RouterLink}
          to={`/orders/${job.id}`}
        >
          <Avatar
            className={classes.avatar}
            size="small"
            style={{ backgroundColor: colors.grey[500] }}
          >
            {job.type === 'Repair' ? <BuildIcon /> : <AssignmentIcon />}
          </Avatar>
        </IconButton>
      </ListItemAvatar>

      <ListItemText primary={shopMap[job.shop]?.name} secondary={job.date} />
      <div className={classes.wrapper}>
        <TextField
          label="Assignee"
          name="assignee"
          fullWidth
          className={classes.select}
          onChange={evt => handleChange(evt, job)}
          select
          SelectProps={{ native: true }}
          disabled={loading || assignBlocked}
          helperText={assignBlocked ? ADDRESS_NEEDS_FIXING : undefined}
          placeholder="Unassigned"
          value={
            (techs.find(tech => job.assignee === tech.id) || {}).id ||
            'Unassigned'
          }
        >
          <option value="Unassigned">Unassigned</option>
          {getSortedTechs(job).map(tech => (
            <option key={tech.id} value={tech.id}>
              {formatAssignTechOption(tech)}
            </option>
          ))}
        </TextField>
        {loading && (
          <CircularProgress size={16} className={classes.selectProgress} />
        )}
      </div>
    </ListItem>
  );
}

function distance(lat1, lon1, lat2, lon2, unit) {
  if (String(lat1) === String(lat2) && String(lon1) === String(lon2)) {
    return 0;
  }
  const radlat1 = (Math.PI * lat1) / 180;
  const radlat2 = (Math.PI * lat2) / 180;
  const theta = lon1 - lon2;
  const radtheta = (Math.PI * theta) / 180;
  let dist =
    Math.sin(radlat1) * Math.sin(radlat2) +
    Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta);
  if (dist > 1) {
    dist = 1;
  }
  dist = Math.acos(dist);
  dist = (dist * 180) / Math.PI;
  dist = dist * 60 * 1.1515;
  if (unit === 'K') {
    dist *= 1.609344;
  }
  if (unit === 'N') {
    dist *= 0.8684;
  }
  return dist;
}

export function assignOptionDistance(tech, job, shopMap) {
  const shops = shopMap || {};
  const jobShop = job && shops[job.shop];
  const jobCenter = shopMapCenter(
    jobShop && jobShop.lat,
    jobShop && jobShop.lng
  );
  const techLocated =
    tech &&
    tech.location &&
    tech.location !== 'none' &&
    tech.location !== 'None';

  if (!jobCenter) {
    return { meters: Number.POSITIVE_INFINITY, broken: true };
  }

  if (!techLocated) {
    return { meters: Number.POSITIVE_INFINITY, broken: false };
  }

  const techShop = shops[tech.location];
  const techCenter = shopMapCenter(
    techShop && techShop.lat,
    techShop && techShop.lng
  );
  if (!techCenter) {
    return { meters: Number.POSITIVE_INFINITY, broken: true };
  }

  return {
    meters: distance(techCenter[0], techCenter[1], jobCenter[0], jobCenter[1]),
    broken: false
  };
}

export function formatAssignTechOption(tech) {
  if (tech.distanceBroken) {
    return `${tech.name} (${ADDRESS_NEEDS_FIXING})`;
  }
  if (Number.isFinite(tech.distance)) {
    return `${tech.name} (${tech.distance.toFixed(1)} m)`;
  }
  return tech.name;
}

OrderAssignListItem.propTypes = {
  className: PropTypes.string,
  jobs: PropTypes.array
};

function GuardedOrderAssignListItem(props) {
  return (
    <OrderErrorBoundary>
      <OrderAssignListItem {...props} />
    </OrderErrorBoundary>
  );
}

GuardedOrderAssignListItem.propTypes = OrderAssignListItem.propTypes;

export default GuardedOrderAssignListItem;
